import { useState, useRef, forwardRef, useImperativeHandle, useLayoutEffect, useCallback, useEffect } from 'react';
import {
    Flex,
    Image,
    Box,
    ImageProps,
    HStack,
    IconButton,
    Tooltip,
    Icon
} from '@chakra-ui/react';
import { TbRotateClockwise, TbZoomIn, TbZoomOut, TbRefresh } from 'react-icons/tb';

export interface ZoomImageHandle {
    zoomIn: () => void;
    zoomOut: () => void;
    reset: () => void;
    rotate: () => void;
    zoomLevel: number;
    /**
     * PDF viewer only: renders every page with the same on-screen tone and downloads a JPEG-based PDF.
     * Helps re-uploads pass server-side brightness checks on very light scans.
     */
    downloadToneAdjustedPdf?: (filenameBase: string) => Promise<void>;
}

/** Normalized 0–1 coordinates on the intrinsic image (same space as Textract bboxes). */
export type ZoomImageHighlightBox = {
    page?: number;
    left: number;
    top: number;
    width: number;
    height: number;
};

interface ZoomImageProps extends ImageProps {
    hideControls?: boolean;
    /** When set, draws a semi-transparent frame over the invoice line (object-fit: contain aware). */
    imageHighlight?: ZoomImageHighlightBox | null;
    /** When true, highlight spans full rendered image width (line-item row mode). */
    imageHighlightFullWidth?: boolean;
}

const ZoomImage = forwardRef<ZoomImageHandle, ZoomImageProps>(({
    hideControls,
    imageHighlight,
    imageHighlightFullWidth = false,
    ...props
}, ref) => {
    const [zoomLevel, setZoomLevel] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [highlightLayout, setHighlightLayout] = useState<{
        cw: number;
        ch: number;
        ox: number;
        oy: number;
        rw: number;
        rh: number;
    } | null>(null);
    const [viewerBox, setViewerBox] = useState({ containerW: 1, containerH: 1, imageW: 1, imageH: 1 });

    const updateHighlightLayout = useCallback(() => {
        const img = imageRef.current;
        const container = containerRef.current;
        if (container) {
            setViewerBox((prev) => {
                const next = {
                    containerW: Math.max(1, container.clientWidth),
                    containerH: Math.max(1, container.clientHeight),
                    imageW: Math.max(1, img?.clientWidth ?? prev.imageW),
                    imageH: Math.max(1, img?.clientHeight ?? prev.imageH),
                };
                if (
                    next.containerW === prev.containerW &&
                    next.containerH === prev.containerH &&
                    next.imageW === prev.imageW &&
                    next.imageH === prev.imageH
                ) {
                    return prev;
                }
                return next;
            });
        }
        if (!img?.naturalWidth || !img.naturalHeight) {
            setHighlightLayout(null);
            return;
        }
        const cw = img.clientWidth;
        const ch = img.clientHeight;
        if (cw < 2 || ch < 2) {
            setHighlightLayout(null);
            return;
        }
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        const r = Math.min(cw / nw, ch / nh);
        const rw = nw * r;
        const rh = nh * r;
        const ox = (cw - rw) / 2;
        const oy = (ch - rh) / 2;
        setHighlightLayout({ cw, ch, ox, oy, rw, rh });
    }, []);

    useLayoutEffect(() => {
        updateHighlightLayout();
    }, [updateHighlightLayout, props.src, zoomLevel, imagePosition, imageHighlight, rotation]);

    useLayoutEffect(() => {
        const onResize = () => updateHighlightLayout();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [updateHighlightLayout]);

    const handleZoomIn = () => {
        setZoomLevel(prev => {
            const newZoom = Math.min(prev + 0.5, 4);
            // Center the image when zooming in
            setImagePosition({ x: 0, y: 0 });
            return newZoom;
        });
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => {
            const newZoom = Math.max(prev - 0.5, 0.5);
            if (newZoom <= 1) {
                setImagePosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    const handleRefresh = () => {
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
        setRotation(0);
    };

    const handleRotate = () => {
        setRotation((prev) => (prev + 90) % 360);
    };

    useEffect(() => {
        setRotation(0);
    }, [props.src]);

    // Expose zoom controls to parent via ref
    useImperativeHandle(ref, () => ({
        zoomIn: handleZoomIn,
        zoomOut: handleZoomOut,
        reset: handleRefresh,
        rotate: handleRotate,
        get zoomLevel() { return zoomLevel; }
    }), [zoomLevel]);

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaY > 0 ? -0.2 : 0.2;

        if (containerRef.current && imageRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();

            // Calculate mouse position relative to container center
            const mouseX = e.clientX - containerRect.left - containerRect.width / 2;
            const mouseY = e.clientY - containerRect.top - containerRect.height / 2;

            setZoomLevel(prev => {
                const newZoom = Math.min(Math.max(prev + delta, 0.5), 4);

                if (delta > 0 && newZoom > prev) {
                    // Zooming in: adjust position to zoom into mouse position
                    const zoomFactor = newZoom / prev;
                    setImagePosition(prevPos => ({
                        x: prevPos.x - (mouseX / prev) * (zoomFactor - 1),
                        y: prevPos.y - (mouseY / prev) * (zoomFactor - 1)
                    }));
                } else if (newZoom <= 1) {
                    // Reset to center when zoomed out to 1x or less
                    setImagePosition({ x: 0, y: 0 });
                }

                return newZoom;
            });
        } else {
            // Fallback: center zoom if refs not available
            setZoomLevel(prev => {
                const newZoom = Math.min(Math.max(prev + delta, 0.5), 4);
                if (newZoom <= 1) {
                    setImagePosition({ x: 0, y: 0 });
                }
                return newZoom;
            });
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setDragStart({
            x: e.clientX - imagePosition.x,
            y: e.clientY - imagePosition.y
        });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging) {
            e.preventDefault();
            e.stopPropagation();
            setImagePosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDoubleClick = () => {
        if (zoomLevel === 1) {
            setZoomLevel(2);
            setImagePosition({ x: 0, y: 0 });
        } else {
            setZoomLevel(1);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const isOrthogonal = rotation % 180 !== 0;
    const visualW = isOrthogonal ? viewerBox.imageH : viewerBox.imageW;
    const visualH = isOrthogonal ? viewerBox.imageW : viewerBox.imageH;
    const rotateFit = visualW > 0 && visualH > 0
        ? Math.min(viewerBox.containerW / visualW, viewerBox.containerH / visualH, 1)
        : 1;

    return (
        <Box
            ref={containerRef}
            overflow="hidden"
            position="relative"
            borderRadius="md"
            onWheel={handleWheel}
            p={hideControls ? 0 : 8}
            h="100%"
        >
            {/* Control Buttons – only if not externally controlled */}
            {!hideControls && (
                <HStack
                    position="absolute"
                    top={4}
                    right={4}
                    zIndex={10}
                    spacing={2}
                    bg="white"
                    borderRadius="md"
                    p={1}
                    boxShadow="none"
                >
                    <Tooltip label="Zoom In">
                        <IconButton
                            aria-label="Zoom In"
                            icon={<Icon as={TbZoomIn as React.ElementType} w={4} h={4} />}
                            size="sm"
                            onClick={handleZoomIn}
                            isDisabled={zoomLevel >= 4}
                            variant="ghost"
                        />
                    </Tooltip>
                    <Tooltip label="Zoom Out">
                        <IconButton
                            aria-label="Zoom Out"
                            icon={<Icon as={TbZoomOut as React.ElementType} w={4} h={4} />}
                            size="sm"
                            onClick={handleZoomOut}
                            isDisabled={zoomLevel <= 0.5}
                            variant="ghost"
                        />
                    </Tooltip>
                    <Tooltip label="Reset View">
                        <IconButton
                            aria-label="Reset View"
                            icon={<Icon as={TbRefresh as React.ElementType} w={4} h={4} />}
                            size="sm"
                            onClick={handleRefresh}
                            variant="ghost"
                        />
                    </Tooltip>
                    <Tooltip label="Rotate 90°">
                        <IconButton
                            aria-label="Rotate 90°"
                            icon={<Icon as={TbRotateClockwise as React.ElementType} w={4} h={4} />}
                            size="sm"
                            onClick={handleRotate}
                            variant="ghost"
                        />
                    </Tooltip>
                </HStack>
            )}

            <Flex
                justify="center"
                align="center"
                width='100%'
                height="100%"
                minH={hideControls ? undefined : "75vh"}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <Box
                    position="relative"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    maxW="100%"
                    maxH="100%"
                    style={{
                        transform: `scale(${zoomLevel * rotateFit}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px) rotate(${rotation}deg)`,
                        transition: isDragging ? 'none' : 'transform 0.2s ease',
                        transformOrigin: 'center center',
                    }}
                >
                    <Box position="relative" display="inline-block" maxW="100%" maxH="100%">
                        <Image
                            {...props}
                            ref={imageRef}
                            objectFit="contain"
                            cursor={isDragging ? 'grabbing' : 'grab'}
                            onMouseDown={handleMouseDown}
                            onDoubleClick={handleDoubleClick}
                            onLoad={(ev) => {
                                updateHighlightLayout();
                                if (typeof props.onLoad === 'function') {
                                    (props.onLoad as React.ReactEventHandler<HTMLImageElement>)(ev);
                                }
                            }}
                            userSelect="none"
                            draggable={false}
                        />
                        {imageHighlight && highlightLayout && (
                            <Box
                                pointerEvents="none"
                                position="absolute"
                                left={`${imageHighlightFullWidth ? highlightLayout.ox : highlightLayout.ox + imageHighlight.left * highlightLayout.rw}px`}
                                top={`${highlightLayout.oy + imageHighlight.top * highlightLayout.rh}px`}
                                w={`${imageHighlightFullWidth ? highlightLayout.rw : imageHighlight.width * highlightLayout.rw}px`}
                                h={`${imageHighlight.height * highlightLayout.rh}px`}
                                bg="rgba(255, 235, 59, 0.32)"
                                boxShadow="inset 0 1px 0 rgba(255, 235, 59, 0.6), inset 0 -1px 0 rgba(255, 235, 59, 0.6)"
                            />
                        )}
                    </Box>
                </Box>
            </Flex>
        </Box>
    );
});

ZoomImage.displayName = 'ZoomImage';

export default ZoomImage;
